// Is this configuration FINISHED? Written to the item as custitem_unfinishedconfiguration.
//
// Kevin, 2026-10-06: "if the item has any field in the fire tube tab or any other tab that is
// not the customer requirements tab then consider it not finished. set the check box
// custitem_unfinishedconfiguration to true." Read as: any field outside Customer Requirements
// left BLANK. A blank on Customer Requirements does not count.
//
// Where unfinished items come from: a QUOTE. A sales order has no "save anyway" and requires
// every section, so it cannot submit a blank; a quote can, and marks most of Firetube
// "(optional)" (see quoteScope). So this judges the form AS A SALES ORDER WOULD -- those
// optional sections count -- because the flag is what tells someone the quote's item still
// needs finishing before it is built. api/notices reads the flag from there: the sales-order
// email and the Monday/Thursday list.
//
// "Required" is exactly what Save & Submit demands on a sales order (collectRequiredVisible):
// hidden fields, optional questions and an untouched FAH section are not missing here either.
// The one addition is a tab not yet REACHED, below.

import { collectRequiredVisible, type UnansweredField } from "./validation";
import { isHidden } from "./rule";
import type { CpqData } from "../data/loader";
import type { AnswerState } from "../types";

/** "Customer Requirements". The one tab whose blanks do not make a configuration unfinished. */
export const CUSTOMER_REQUIREMENTS_TAB_CODE = "INPUTS_TAB";

/** Answer keys a parsed hide rule reads: __["HOUSING/HOUSING"] -> HOUSING/HOUSING. */
function ruleInputs(parsed: string): string[] {
  return [...parsed.matchAll(/__\["([^"]+)"\]/g)].map((m) => m[1]);
}

const isBlank = (v: unknown) => v === undefined || v === null || v === false || String(v).trim() === "";

/** Every reason this configuration is unfinished, in the order the UI shows the fields.
 *  Empty means finished. */
export function findUnfinishedFields(
  data: CpqData,
  answers: AnswerState,
  // An MB middle burner's In / Up cells (validation requiredCells).
  requiredCells?: readonly string[]
): UnansweredField[] {
  const out: UnansweredField[] = [];

  // A TAB NOT YET REACHED. Firetube stays hidden until a housing is picked on Customer
  // Requirements, and a hidden tab has no visible fields to be blank -- so without this, a
  // configuration that never got as far as Firetube would read as finished.
  //
  // Only while the answers its hide rule reads are ALL blank: that is "nobody has made the
  // choice that decides this tab yet". Once one is answered the tab is decided, and a tab
  // hidden by a real answer (a T-Type housing has no Firetube tab) is correctly empty.
  for (const tab of data.tabs) {
    const code = tab.custrecord_cpqc_tab_code;
    if (code === CUSTOMER_REQUIREMENTS_TAB_CODE || code === "DEBUG") continue;
    const parsed = (tab.custrecord_cpqc_tab_parsed_hide_rule ?? "").trim();
    if (!parsed || parsed === "true") continue; // always shown, or never shown at all
    const rule = { hideRule: tab.custrecord_cpqc_tab_hide_rule, parsedHideRule: parsed };
    if (!isHidden(rule, answers)) continue;
    const inputs = ruleInputs(parsed);
    if (inputs.length > 0 && inputs.every((k) => isBlank(answers[k]))) {
      out.push({ code: "", label: "not started", tab: tab.name, tabId: tab.id });
    }
  }

  const crTabIds = new Set(
    data.tabs.filter((t) => t.custrecord_cpqc_tab_code === CUSTOMER_REQUIREMENTS_TAB_CODE).map((t) => t.id)
  );
  for (const f of collectRequiredVisible(data, answers, { asSalesOrder: true, requiredCells })) {
    if (f.answered || crTabIds.has(f.tabId)) continue;
    out.push({ code: f.code, label: f.label, tab: f.tab, tabId: f.tabId });
  }
  return out;
}
